import { Card, CardContent, Grid, Link, Typography, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { deleteIcon, hasJiraIcon } from "../../assets";
import { formatTimestamp } from "../../helpers/date.helper";
import { SearchInput } from "../searchInput.ui";
import { TestResultStatusGrid } from "../testResultStatusTable.ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CHANNELS, CONFIGNAME } from "../../types/global.enum";
import { useSelector } from "react-redux";
import { getCurrentProject, getCurrentTestCase } from "../../store/selectors/project.selector";
import { getScheduledTestResultlistDetails, getTestResultDetails, getTestResultlistDetails } from "../../services/testresults.service";
import { setCurrentTestCase, setCurrentTestResultDetails } from "../../store/resuders/project.reducer";
import { useAppDispatch } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { appConfigSelector } from "../../store/selectors/app.selector";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Modal } from "../modal";
import { JiraTicket } from "../../views/jira/jira.component";
import { uniqueId } from "../../helpers/form.helper";


export interface IProps {
    projectId : any;
    testcaseId : any;
    openTestResultsDetailsView: () => void;
}

export const TestCaseList = ({
    projectId,
    testcaseId,
    openTestResultsDetailsView
}: IProps) => {
    const { t } = useTranslation();
    const history = useNavigate();
     const [openJiraModal, setOpenJiraModal] = useState<string>('');
     const appConfig = useSelector(appConfigSelector);
    const project = useSelector(getCurrentProject);
    const [data, setData] = useState<any>([]);
    const [filteredData, setFilteredData] = useState<any>([]);
    const dispatch = useAppDispatch();
    const [testResultDetails, setTestResultDetails] = useState<any>(null);

    const [criteria, setCriteria] = useState<any>({
        sort: '-scheduleStartTime',
        pageNo: 0,
        pageSize: 10,
        filter: ''
    });

    useEffect(() => {
        loadData();
    }, [projectId, testcaseId])

    useEffect(() => {
        var direction = criteria.sort.charAt(0) === '+'? "ASC" : "DESC";
        setFilteredData((previousData: any) => previousData?.sort((a: any, b: any) => {
            if (direction && criteria.sort) {
                const aValue = a[criteria.sort.substring(1)]?.toString() || ''; 
                const bValue = b[criteria.sort.substring(1)]?.toString() || '';
                if (direction === 'ASC') {
                    return aValue.localeCompare(bValue);
                } else {
                    return bValue.localeCompare(aValue);
                }
            }
            return 0;
        }));
    }, [criteria])

    const hasJiraEnabled = useMemo(()=>appConfig?.some(c=>c.configName === CONFIGNAME.JIRA && c.configValue) ,[appConfig])

    const createJiraTicket = ()=> {
        setOpenJiraModal(uniqueId());
    }

   const loadJiraTicket = (params:any)=> async ()=> {

    setTestResultDetails({
        ...params,
        name:params.testCaseName,
        testResult: {
            id:params.resultId,
            schedule:params.scheduleId,
        }
    })
        const { data } = await getTestResultDetails(params?.resultId, +(project?.testProjectID || projectId || 1), project.data.channelType);
        dispatch(setCurrentTestResultDetails(data));

        if(!params.ignore) {
            createJiraTicket();
        }
    }

    const closeJiraModal = useCallback(async (saved?:boolean)=> {
        setOpenJiraModal('');

        if(saved) {
            loadData();
        }
    },[]);

    const loadData = async () => {
        const { data } = await getScheduledTestResultlistDetails(projectId ,testcaseId, project.data.channelType);
        setData(data);
        setFilteredData(data);
    }

    const gotoTestCaseDetailsPage = (testResult: any) => async () => {
        const { data } = await getTestResultDetails(testResult?.resultId, +(project?.testProjectID || projectId || 1), project.data.channelType);
        dispatch(setCurrentTestResultDetails(data));
        history(`/projects/${project?.testProjectID || projectId}/scheduled/testresults/${testResult.scheduleId}/${testResult?.resultId}`);
        openTestResultsDetailsView();
    }

    // Columns for Test Results Status Page
    const testStatusColumns: any[] = [
        { caption: 'ID', width: '10%', fieldName: 'resultId', sortable: true, key: true },
        {   caption: '', width: '1%', key: true,
            redner: (params: any) => 
                <div className="flex-center">
                {params.comment && (
                 <Tooltip title={params.comment}  arrow>
                    <InsertCommentIcon style={{ fontSize: '20px' }} />
                </Tooltip>
                )}
                </div>
        },
        hasJiraEnabled ? {
            caption: t('Jira'), width: '5%',
            redner: (params: any) => (
                <div className="flex-center">                    
                    {params.jiraTicket ? <div onClick={loadJiraTicket(params)} className='jira-ticket-link'><img src={hasJiraIcon} alt=""/></div> : <AddCircleOutlineIcon onClick={loadJiraTicket(params)}  className="blue-icon"/>}
                </div>
            ),
        }:{width: '0.1%'},
        {
            fieldName: 'testCaseName', caption: t('TEST CASE NAME'), width: '30%', sortable: true,
            redner: (params: any) => <div className="flex-center">
                <Link onClick={gotoTestCaseDetailsPage(params)} className="a-link-black">{params.testCaseName}</Link></div>
        },
        {
            fieldName: 'startTime', caption: t('DATE TIME'), width: '15%', sortable: true,
            redner: (params: any) => {
                const startTime = new Date(params.startTime);
                return (formatTimestamp(startTime))
            }
        },
        {
            fieldName: 'status', caption: t('STATUS'), width: '15%', sortable: true,
            redner: (params: any) => (
            <div style={{display: 'flex', alignItems: 'center'}}>
                {/* {params?.status === 'FAILED' ? <div className="last-run-status-filled-circle last-run-status-filled-red-circle"></div> : 
                params?.status === 'RUNNING' ? <div className="last-run-status-filled-circle last-run-status-filled-yellow-circle"></div> :
                <div className="last-run-status-filled-circle last-run-status-filled-green-circle"></div>
                } */}
                
                <span style={{ color: params?.status === 'FAILED' || params?.status === 'FORCE_STOPPED' || params?.status === 'TIMEOUT_STOPPED' ? 'red' : params?.status === 'RUNNING' ? 'black' : '#58A148' }}>
                    {params?.status === "TIMEOUT_STOPPED" ? "TIMEOUT" : params?.status}
                </span>
            </div>
                
            )
        },
    ];

    const sortChanged = (column: string, direction: string) => {
        setCriteria({
            ...criteria,
            pageNo: 0,
            sort: `${direction === 'ASC' ? '+' : '-'}${column}`,
        });
    };

    const onFilterChanged = (filter: string) => {
        if(filter) {
            filter = filter.toLowerCase();
            setFilteredData(data?.filter((d : any) => d.testCaseName.toLowerCase().includes(filter)));
        } else {
            setFilteredData(data);
        }
        
        // console.log(filter);
        // setCriteria({
        //     ...criteria,
        //     pageNo: 0,
        //     filter
        // });
    }

    return (
        <Grid item xs={12} paddingRight={'9px'} style={{overflowY: 'auto'}}>
            <Card>
                <CardContent>
                    <div className='test-result-header'>
                        <div className="action-header">                                                                            
                        </div>
                        <div className="test-results-filters">
                            <SearchInput onFilter={onFilterChanged} />
                        </div>
                    </div>
                    <TestResultStatusGrid
                        totalPages={-1}
                        sortChanged={sortChanged}
                        columns={testStatusColumns}
                        data={
                            filteredData || []
                        }
                        sortBy={criteria.sort}
                    />
                </CardContent>
            </Card>
            <Modal
                isOpen={openJiraModal}
                className={'full-width'}
                title={testResultDetails?.testResult?.jiraTicket ? t("Modify Jira Ticket"):t("Create a Jira Ticket")}
                body={<JiraTicket scheduled={testResultDetails?.scheduleId} testResultDetails={testResultDetails} project={project}  cancel={closeJiraModal} channel={project?.data?.channelType}/>}
            />
        </Grid>
    );
}
